import { useMemo, useState } from 'react';

import {
  Badge,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  ToggleButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Clock, Search, SlidersHorizontal, X } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import {
  type ActivityFiltersActivityFragment,
  type Activity_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import {
  type ActivityParticipant,
  ActivityParticipantFilter,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivityParticipantFilter';
import { ActivitySearchBar } from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivitySearchBar';
import {
  type ActivitySortOrder,
  ActivitySortSelect,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivitySortSelect';
import { ActivityTypeFilter } from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivityTypeFilter';
import { FilterSelectIcon } from '@modules/_shared/components/feed-v2/components/ActivityFilters/FilterSelectIcon';
import {
  getFilterSelectStyles,
  menuItemStyles,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/filterSelectStyles';
import { HorizontalScroller } from '@modules/_shared/components/feed-v2/components/ActivityFilters/HorizontalScroller';
import {
  type DateRange,
  DateRangeSelect,
} from '@modules/_shared/components/feed-v2/components/DateRangeSelect';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_FILTERS_FRAGMENT = gql(/* GraphQL */ `
  fragment ActivityFiltersActivity on activities {
    id
    activity_type
    lead_id
    creator {
      ...UserAvatar
    }
    assignee {
      ...UserAvatar
    }
    lead {
      contact {
        id
        full_name
        user_images(
          order_by: [{ is_primary: desc }, { order_nr: asc }]
          limit: 1
        ) {
          image {
            url
          }
        }
      }
    }
  }
`);

type ActivityFiltersProps = {
  activities: ActivityFiltersActivityFragment[];
  selectedTypes: Activity_Types_Enum_Enum[];
  onSelectedTypesChange: (types: Activity_Types_Enum_Enum[]) => void;
  selectedParticipants: string[];
  onSelectedParticipantsChange: (participants: string[]) => void;
  leadFilter: string;
  onLeadFilterChange: (filter: string) => void;
  onSearchTermChange: (term: string) => void;
  onOrderChange: (sortOrder: ActivitySortOrder) => void;
  primaryLeadId?: string;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  overdue?: boolean;
  onOverdueChange?: (overdue: boolean) => void;
  disabled?: boolean;
};

const ACTIVITY_FILTERS_HEIGHT = 35;

export const ActivityFilters = ({
  activities,
  selectedTypes,
  onSelectedTypesChange,
  selectedParticipants,
  onSelectedParticipantsChange,
  leadFilter,
  onLeadFilterChange,
  onSearchTermChange,
  onOrderChange,
  primaryLeadId,
  dateRange,
  onDateRangeChange,
  overdue = false,
  onOverdueChange,
  disabled = false,
}: ActivityFiltersProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtersVisible, setFiltersVisible] = useState(!isMobile);
  const [showSearchBar, setShowSearchBar] = useState(false);

  const activeFilterCount = useMemo(
    () =>
      [
        selectedTypes.length > 0,
        selectedParticipants.length > 0,
        leadFilter !== 'all',
      ].filter(Boolean).length,
    [selectedTypes, selectedParticipants, leadFilter],
  );

  const activityCounts = useMemo(
    () =>
      activities.reduce((acc, activity) => {
        acc[activity.activity_type] = (acc[activity.activity_type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
    [activities],
  );

  const linkedLeadsCount = useMemo(() => {
    if (!primaryLeadId) {
      return 0;
    }
    const linkedActivities = activities.filter(
      a => a.lead_id && a.lead_id !== primaryLeadId,
    );

    return new Set(linkedActivities.map(a => a.lead_id)).size;
  }, [activities, primaryLeadId]);

  const uniqueParticipants = useMemo(
    () =>
      [
        ...new Map(
          activities.reduce<[string, ActivityParticipant][]>((acc, act) => {
            if (act.assignee) {
              acc.push([
                act.assignee.id,

                {
                  id: act.assignee.id,
                  name: act.assignee.full_name ?? '',
                  imageUrl: act.assignee.user_images?.[0]?.image?.url ?? null,
                },
              ]);
            }

            if (act.lead?.contact) {
              acc.push([
                act.lead.contact.id,
                {
                  id: act.lead.contact.id,
                  name: act.lead.contact.full_name ?? '',
                  imageUrl:
                    act.lead.contact.user_images?.[0]?.image?.url ?? null,
                },
              ]);
            }

            // `change` activities (lot updates) have no assignee or lead
            // contact — the creator is the only meaningful participant. For
            // every other activity type, the assignee/contact paths above are
            // authoritative; falling back to the creator would surface noisy
            // entries (e.g. agents who only created an activity but weren't
            // the assignee).
            if (act.activity_type === 'change' && act.creator) {
              acc.push([
                act.creator.id,
                {
                  id: act.creator.id,
                  name: act.creator.full_name ?? '',
                  imageUrl: act.creator.user_images?.[0]?.image?.url ?? null,
                },
              ]);
            }

            return acc;
          }, []),
        ).values(),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [activities],
  );

  const filterControls = (
    <>
      <ActivityTypeFilter
        selectedTypes={selectedTypes}
        onFilter={onSelectedTypesChange}
        activityCounts={activityCounts}
      />

      <ActivityParticipantFilter
        selectedParticipants={selectedParticipants}
        onFilter={onSelectedParticipantsChange}
        uniqueParticipants={uniqueParticipants}
      />

      {linkedLeadsCount > 0 && (
        <FormControl
          size="small"
          sx={{
            width: 'auto',
            minWidth: 0,
          }}
        >
          <Select
            value={leadFilter}
            onChange={e => onLeadFilterChange(e.target.value as 'all' | 'this')}
            IconComponent={FilterSelectIcon}
            input={<OutlinedInput />}
            renderValue={selected => {
              if (selected === 'this') {
                return t('This lead');
              }

              return t('All leads');
            }}
            MenuProps={{
              slotProps: {
                paper: { sx: { mt: 0.5 } },
              },
            }}
            sx={getFilterSelectStyles(leadFilter !== 'all')}
          >
            <MenuItem value="all" sx={menuItemStyles}>
              <Typography variant="body2">
                {t('All leads')} ({linkedLeadsCount + 1})
              </Typography>
            </MenuItem>
            <MenuItem value="this" sx={menuItemStyles}>
              <Typography variant="body2">{t('This lead')}</Typography>
            </MenuItem>
          </Select>
        </FormControl>
      )}

      <ActivitySortSelect onSortOrderChange={onOrderChange} />

      {onOverdueChange != null && (
        <ToggleButton
          value="overdue"
          color="primary"
          size="small"
          selected={overdue}
          onChange={() => onOverdueChange(!overdue)}
          sx={{
            height: ACTIVITY_FILTERS_HEIGHT,
            flexShrink: 0,
            gap: 0.5,
            px: 1,
            borderRadius: '8px',
            borderColor: `${theme.palette.divider} !important`,
            bgcolor: 'white.main',
            color: 'text.secondary',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '&.Mui-selected': {
              borderColor: `${theme.palette.primary.main} !important`,
            },
          }}
        >
          <Clock size={14} />
          <Typography variant="body2">{t('Overdue')}</Typography>
        </ToggleButton>
      )}

      {dateRange != null && onDateRangeChange != null && (
        <DateRangeSelect value={dateRange} onChange={onDateRangeChange} />
      )}

      {activeFilterCount > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          onClick={() => {
            onSelectedTypesChange([]);
            onSelectedParticipantsChange([]);
            onLeadFilterChange('all');
          }}
          sx={{
            alignItems: 'center',
            color: 'text.secondary',
            cursor: 'pointer',
            alignSelf: 'center',
            whiteSpace: 'nowrap',

            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <X size={12} />
          <Typography variant="caption">{t('Clear filters')}</Typography>
        </Stack>
      )}
    </>
  );

  return (
    <Box
      sx={{
        width: '100%',
        ...(disabled && {
          opacity: 0.5,
          pointerEvents: 'none',
        }),
      }}
    >
      {showSearchBar && (
        <ActivitySearchBar
          onSearch={onSearchTermChange}
          onBlur={() => setShowSearchBar(false)}
          onClear={() => {
            onSearchTermChange('');
            setShowSearchBar(false);
          }}
        />
      )}
      {!showSearchBar && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <ToggleButton
            value={true}
            color="primary"
            size="small"
            selected={filtersVisible}
            onChange={() => setFiltersVisible(prevSelected => !prevSelected)}
            sx={{
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderRadius: '8px',
              width: ACTIVITY_FILTERS_HEIGHT,
              height: ACTIVITY_FILTERS_HEIGHT,
              borderColor: `${theme.palette.divider} !important`,
              bgcolor: 'white.main',
              transition: 'all 0.15s ease',
              '&.Mui-selected': {
                borderColor: `${theme.palette.primary.main} !important`,
              },
            }}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              invisible={filtersVisible || activeFilterCount === 0}
              sx={{
                '& .MuiBadge-badge': {
                  top: '-8px',
                  right: '-8px',
                  padding: '0 4px',
                  minWidth: '15px',
                  height: '15px',
                  fontSize: '10px',
                },
              }}
            >
              <SlidersHorizontal size={16} />
            </Badge>
          </ToggleButton>

          {filtersVisible &&
            (isMobile ? (
              <Stack
                direction="column"
                spacing={1}
                sx={{ flex: 1, mx: 1, minWidth: 0 }}
              >
                {filterControls}
              </Stack>
            ) : (
              <HorizontalScroller>{filterControls}</HorizontalScroller>
            ))}

          <IconButton
            size="small"
            onClick={() => setShowSearchBar(true)}
            sx={{
              flexShrink: 0,
              width: ACTIVITY_FILTERS_HEIGHT,
              height: ACTIVITY_FILTERS_HEIGHT,
              borderRadius: '8px',
              bgcolor: 'white.main',
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'all 0.15s ease',
            }}
          >
            <Search size={16} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
};
